import { createRoot } from 'react-dom/client'
import { Provider } from 'react-redux'
// import * as serviceWorkerRegistration from './serviceWorkerRegistration'
import store from './store'
import App from './App'
import LocalizationProvider from '@mui/lab/LocalizationProvider'
import * as Sentry from '@sentry/react'
import { Integrations } from '@sentry/tracing'
import AdapterDayjs from '@mui/lab/AdapterDayjs'
import { version } from '../package.json'
import getEnv, { isProduction } from './utilities/getEnv'
import 'fontsource-roboto'
import 'Services/localize/i18n'
import { BrowserRouter } from 'react-router-dom'
import { PostHogProvider } from 'posthog-js/react'
import PostHog from 'posthog-js'
import { enrichSentryErrorEvent } from 'utilities/sentryLogUtil'
import { t } from 'i18next'

if (isProduction()) {
    const serverEnvironment = getEnv('SERVER_SIDE_ENVIRONMENT')
    if (
        serverEnvironment !== undefined &&
        ['Testing', 'UAT', 'Production'].some((x) => x === serverEnvironment)
    ) {
        const overridedVersion = getEnv('WEB_VERSION') // overrided version to display instead of from package.json if any
        Sentry.init({
            dsn:
                'https://c58ae6cd06a2484b8463242be43cdd3f@o568528.ingest.sentry.io/5715202',
            integrations: [
                new Integrations.BrowserTracing(),
                new PostHog.SentryIntegration(
                    PostHog,
                    'mycos-technologies',
                    5715202,
                ),
            ],
            release:
                'orderwire-pos@' +
                (overridedVersion ? overridedVersion : version),
            environment: serverEnvironment,

            // of transactions for performance monitoring.
            // We recommend adjusting this value in production
            beforeSend(event, hint) {
                // ignore logging Axios Network error
                if (hint.originalException) {
                    const originalException = hint.originalException as any
                    const exceptionMessage = originalException.message

                    // Block sending HttpError to Sentry
                    if (originalException.httpMetadata) {
                        // log Error message of HttpError
                        console.log(t(exceptionMessage))
                        return null
                    }

                    if (
                        originalException.isAxiosError &&
                        exceptionMessage &&
                        exceptionMessage.toLowerCase().includes('network error')
                    )
                        // return null to block sending error to Sentry
                        return null
                }

                // otherwire, log an error as normally

                // enrich error event, grouping
                enrichSentryErrorEvent(event, hint)

                // return modified event to Sentry
                return event
            },

            // Set tracesSampleRate to 1.0 to capture 100%
            tracesSampleRate: 1.0,

            // ignore error of 401 axios response
            ignoreErrors: [
                'Request failed with status code 401',
                'Request failed with status code 403',
                // ignore dynamic loading error via React.lazy()
                'Failed to fetch dynamically imported module',
                // ignore some signalR errors which are out-of-control factor (e.g., user's internet connection)
                'Failed to complete negotiation with the server',
                // https://sentry.io/answers/react-resizeobserver-loop-completed-with-undelivered-notifications/
                // I decided to ignore this error, because we didn't use this Browser API `ResizeObserver` directly in this project
                // So, it properly caused from another library.
                // However, if this cause the problem to user's experience (users report us). We will take a look on this later.
                'ResizeObserver loop',
            ],
        })
    }
}

// ReactDOM.render(
//     <Provider store={store}>
//         <LocalizationProvider dateAdapter={AdapterDayjs}>
//             <BrowserRouter>
//                 <App />
//             </BrowserRouter>
//         </LocalizationProvider>
//     </Provider>,
//     document.getElementById('root'),
// )
const serverEnvironment = getEnv('SERVER_SIDE_ENVIRONMENT')
createRoot(document.getElementById('root')!).render(
    <Provider store={store}>
        <PostHogProvider
            apiKey={getEnv('REACT_APP_PUBLIC_POSTHOG_KEY')}
            options={{
                api_host: getEnv('REACT_APP_PUBLIC_POSTHOG_HOST'),
                loaded: (ph) => {
                    // Only capture events in Production, UAT, and Testing.
                    if (
                        serverEnvironment === 'Production' ||
                        serverEnvironment === 'UAT' ||
                        serverEnvironment === 'Testing'
                    ) {
                        ph.opt_in_capturing()
                    } else {
                        ph.opt_out_capturing()
                    }
                },
                session_recording: {
                    // Unmask all input fields except for passwords.
                    // https://posthog.com/docs/session-replay/privacy#input-elements
                    maskAllInputs: false,
                    maskInputOptions: {
                        password: true,
                    },
                },
            }}
        >
            <LocalizationProvider dateAdapter={AdapterDayjs}>
                <BrowserRouter>
                    <App />
                </BrowserRouter>
            </LocalizationProvider>
        </PostHogProvider>
    </Provider>,
)

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
// serviceWorkerRegistration.register()
